import React, { useState } from 'react';
import {
  FileText, Search, Filter, Download, Eye, Calendar, Package,
  CheckCircle, Clock, AlertCircle, X, Plus, Edit2, Trash2,
  ChevronRight, Users, Weight, MapPin, Printer, Link2, Unlink
} from 'lucide-react';

// Mock BOM (Operational Manifests) data
const mockBOMs = [
  {
    id: 'bom-001',
    code: 'BOM-2025-12-15',
    date: 'Dec 15, 2025',
    status: 'in_progress',
    intake_session_id: 'sess-001',
    total_packages: 47,
    total_weight: 523.4,
    linked_packages: 42,
    unlinked_packages: 5,
    manifest_code: 'MAN-2025-12-15',
    created_by: 'John Doe',
    notes: 'Regular intake session - morning shipment'
  },
  {
    id: 'bom-002',
    code: 'BOM-2025-12-14',
    date: 'Dec 14, 2025',
    status: 'completed',
    intake_session_id: 'sess-002',
    total_packages: 82,
    total_weight: 945.8,
    linked_packages: 82,
    unlinked_packages: 0,
    manifest_code: 'MAN-2025-12-14',
    created_by: 'Jane Smith',
    notes: 'Large batch - holiday rush'
  },
  {
    id: 'bom-003',
    code: 'BOM-2025-12-13',
    date: 'Dec 13, 2025',
    status: 'completed',
    intake_session_id: 'sess-003',
    total_packages: 63,
    total_weight: 712.2,
    linked_packages: 63,
    unlinked_packages: 0,
    manifest_code: 'MAN-2025-12-13',
    created_by: 'John Doe',
    notes: ''
  },
  {
    id: 'bom-004',
    code: 'BOM-2025-12-12',
    date: 'Dec 12, 2025',
    status: 'invoiced',
    intake_session_id: 'sess-004',
    total_packages: 55,
    total_weight: 628.5,
    linked_packages: 55,
    unlinked_packages: 0,
    manifest_code: 'MAN-2025-12-12',
    created_by: 'Jane Smith',
    notes: 'Express shipment batch',
    invoice_id: 'INV-FS-8521'
  },
];

// Mock packages for a BOM
const mockBOMPackages = [
  { id: 'pkg-001', tracking: 'TRK-2025-45821', bolt: 'BOLT-FS-4521', customer: 'Marcus Johnson', weight: 12.5, status: 'linked' },
  { id: 'pkg-002', tracking: 'TRK-2025-45820', bolt: 'BOLT-FS-4520', customer: 'Sarah Williams', weight: 8.2, status: 'linked' },
  { id: 'pkg-003', tracking: 'TRK-2025-45819', bolt: 'BOLT-FS-4519', customer: 'James Brown', weight: 22.1, status: 'unlinked' },
  { id: 'pkg-004', tracking: 'TRK-2025-45818', bolt: 'BOLT-FS-4518', customer: 'Emily Davis', weight: 5.8, status: 'linked' },
  { id: 'pkg-005', tracking: 'TRK-2025-45817', bolt: 'BOLT-FS-4517', customer: 'Michael Chen', weight: 15.3, status: 'linked' },
];

const statusConfig = {
  in_progress: { label: 'In Progress', color: 'blue', icon: Clock },
  completed: { label: 'Completed', color: 'lime', icon: CheckCircle },
  invoiced: { label: 'Invoiced', color: 'purple', icon: FileText },
};

export default function TenantBOM() {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBOM, setSelectedBOM] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('packages');

  const filteredBOMs = mockBOMs.filter(bom => {
    const matchesFilter = filter === 'all' || bom.status === filter;
    const matchesSearch = 
      bom.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bom.manifest_code.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: mockBOMs.length,
    inProgress: mockBOMs.filter(b => b.status === 'in_progress').length,
    completed: mockBOMs.filter(b => b.status === 'completed').length,
    invoiced: mockBOMs.filter(b => b.status === 'invoiced').length,
  };

  const openDetail = (bom) => {
    setSelectedBOM(bom);
    setShowDetailModal(true);
  };

  return (
    <div className="tenant-bom-page" data-testid="tenant-bom">
      {/* Page Header */}
      <div className="page-header-section">
        <div className="header-content">
          <h2>Operational Manifests (BOM)</h2>
          <p>Manage intake sessions and link packages to operational manifests</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
          data-testid="create-bom-btn"
        >
          <Plus size={16} /> Create BOM
        </button>
      </div>

      {/* Stats Cards */}
      <div className="bom-stats-grid">
        <div className="stat-card" data-testid="stat-total">
          <FileText size={20} />
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total BOMs</span>
          </div>
        </div>
        <div className="stat-card blue" data-testid="stat-in-progress">
          <Clock size={20} />
          <div className="stat-content">
            <span className="stat-value">{stats.inProgress}</span>
            <span className="stat-label">In Progress</span>
          </div>
        </div>
        <div className="stat-card lime" data-testid="stat-completed">
          <CheckCircle size={20} />
          <div className="stat-content">
            <span className="stat-value">{stats.completed}</span>
            <span className="stat-label">Completed</span>
          </div>
        </div>
        <div className="stat-card purple" data-testid="stat-invoiced">
          <FileText size={20} />
          <div className="stat-content">
            <span className="stat-value">{stats.invoiced}</span>
            <span className="stat-label">Invoiced</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bom-toolbar">
        <div className="toolbar-left">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search BOM code, manifest..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="bom-search"
            />
          </div>
          
          <div className="filter-buttons">
            {['all', 'in_progress', 'completed', 'invoiced'].map((f) => (
              <button
                key={f}
                className={`filter-btn ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
              >
                {f === 'all' ? 'All' : statusConfig[f]?.label || f}
              </button>
            ))}
          </div>
        </div>
        
        <div className="toolbar-right">
          <button className="btn btn-ghost" data-testid="export-boms">
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* BOM List */}
      <div className="bom-list">
        {filteredBOMs.map((bom) => {
          const StatusIcon = statusConfig[bom.status]?.icon || Clock;
          return (
            <div 
              key={bom.id} 
              className={`bom-card ${bom.status}`}
              data-testid={`bom-${bom.id}`}
            >
              <div className="bom-card-main">
                <div className="bom-info">
                  <div className="bom-code-section">
                    <FileText size={20} />
                    <div>
                      <span className="bom-code">{bom.code}</span>
                      <span className="manifest-code">{bom.manifest_code}</span>
                    </div>
                  </div>
                  
                  <div className="bom-meta">
                    <div className="meta-item">
                      <Calendar size={14} />
                      <span>{bom.date}</span>
                    </div>
                    <div className="meta-item">
                      <Users size={14} />
                      <span>{bom.created_by}</span>
                    </div>
                  </div>
                </div>

                <div className="bom-stats">
                  <div className="bom-stat">
                    <Package size={16} />
                    <div>
                      <span className="stat-value">{bom.total_packages}</span>
                      <span className="stat-label">Packages</span>
                    </div>
                  </div>
                  <div className="bom-stat">
                    <Weight size={16} />
                    <div>
                      <span className="stat-value">{bom.total_weight} lbs</span>
                      <span className="stat-label">Total Weight</span>
                    </div>
                  </div>
                  <div className="bom-stat linked">
                    <Link2 size={16} />
                    <div>
                      <span className="stat-value">{bom.linked_packages}</span>
                      <span className="stat-label">Linked</span>
                    </div>
                  </div>
                  {bom.unlinked_packages > 0 && (
                    <div className="bom-stat unlinked">
                      <Unlink size={16} />
                      <div>
                        <span className="stat-value">{bom.unlinked_packages}</span>
                        <span className="stat-label">Unlinked</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bom-status-section">
                  <span className={`status-badge ${statusConfig[bom.status]?.color}`}>
                    <StatusIcon size={14} />
                    {statusConfig[bom.status]?.label}
                  </span>
                  {bom.invoice_id && (
                    <span className="invoice-ref">
                      {bom.invoice_id}
                    </span>
                  )}
                </div>
              </div>

              <div className="bom-card-actions">
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => openDetail(bom)}
                  data-testid={`view-${bom.id}`}
                >
                  <Eye size={14} /> View Details
                </button>
                {bom.status === 'in_progress' && (
                  <button className="btn btn-ghost btn-sm" data-testid={`edit-${bom.id}`}>
                    <Edit2 size={14} /> Edit
                  </button>
                )}
                {bom.status === 'completed' && (
                  <button className="btn btn-primary btn-sm" data-testid={`invoice-${bom.id}`}>
                    <FileText size={14} /> Generate Invoice
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedBOM && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div 
            className="modal-content bom-detail-modal large"
            onClick={(e) => e.stopPropagation()}
            data-testid="bom-detail-modal"
          >
            <div className="modal-header">
              <div>
                <h3>{selectedBOM.code}</h3>
                <span className="modal-subtitle">{selectedBOM.manifest_code}</span>
              </div>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="modal-tabs">
              <button 
                className={`tab ${activeTab === 'packages' ? 'active' : ''}`}
                onClick={() => setActiveTab('packages')}
              >
                <Package size={14} /> Packages ({selectedBOM.total_packages})
              </button>
              <button 
                className={`tab ${activeTab === 'summary' ? 'active' : ''}`}
                onClick={() => setActiveTab('summary')}
              >
                <FileText size={14} /> Summary
              </button>
            </div>

            <div className="modal-body">
              {activeTab === 'packages' && (
                <div className="packages-tab">
                  <div className="packages-toolbar">
                    <div className="search-box small">
                      <Search size={14} />
                      <input type="text" placeholder="Search packages..." />
                    </div>
                    <div className="filter-chips">
                      <span className="chip active">All ({mockBOMPackages.length})</span>
                      <span className="chip">Linked ({mockBOMPackages.filter(p => p.status === 'linked').length})</span>
                      <span className="chip">Unlinked ({mockBOMPackages.filter(p => p.status === 'unlinked').length})</span>
                    </div>
                  </div>

                  <div className="packages-table">
                    <div className="table-header">
                      <span>Tracking #</span>
                      <span>BOLT #</span>
                      <span>Customer</span>
                      <span>Weight</span>
                      <span>Status</span>
                      <span>Actions</span>
                    </div>
                    {mockBOMPackages.map((pkg) => (
                      <div key={pkg.id} className="table-row" data-testid={`pkg-${pkg.id}`}>
                        <span className="mono">{pkg.tracking}</span>
                        <span className="mono">{pkg.bolt}</span>
                        <span>{pkg.customer}</span>
                        <span>{pkg.weight} lbs</span>
                        <span className={`link-status ${pkg.status}`}>
                          {pkg.status === 'linked' ? (
                            <><Link2 size={12} /> Linked</>
                          ) : (
                            <><Unlink size={12} /> Unlinked</>
                          )}
                        </span>
                        <div className="actions">
                          {pkg.status === 'unlinked' ? (
                            <button className="btn btn-sm btn-primary" data-testid={`link-${pkg.id}`}>
                              <Link2 size={12} /> Link
                            </button>
                          ) : (
                            <button className="btn btn-sm btn-ghost" data-testid={`unlink-${pkg.id}`}>
                              <Unlink size={12} /> Unlink
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'summary' && (
                <div className="summary-tab">
                  <div className="summary-grid">
                    <div className="summary-card">
                      <h4>BOM Information</h4>
                      <div className="info-list">
                        <div className="info-item">
                          <span className="label">BOM Code</span>
                          <span className="value">{selectedBOM.code}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Manifest Code</span>
                          <span className="value">{selectedBOM.manifest_code}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Date</span>
                          <span className="value">{selectedBOM.date}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Created By</span>
                          <span className="value">{selectedBOM.created_by}</span>
                        </div>
                        <div className="info-item">
                          <span className="label">Status</span>
                          <span className={`status-badge ${statusConfig[selectedBOM.status]?.color}`}>
                            {statusConfig[selectedBOM.status]?.label}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="summary-card">
                      <h4>Package Statistics</h4>
                      <div className="stats-grid">
                        <div className="stat-box">
                          <span className="stat-value">{selectedBOM.total_packages}</span>
                          <span className="stat-label">Total Packages</span>
                        </div>
                        <div className="stat-box">
                          <span className="stat-value">{selectedBOM.total_weight} lbs</span>
                          <span className="stat-label">Total Weight</span>
                        </div>
                        <div className="stat-box lime">
                          <span className="stat-value">{selectedBOM.linked_packages}</span>
                          <span className="stat-label">Linked</span>
                        </div>
                        <div className="stat-box amber">
                          <span className="stat-value">{selectedBOM.unlinked_packages}</span>
                          <span className="stat-label">Unlinked</span>
                        </div>
                      </div>
                    </div>

                    {selectedBOM.notes && (
                      <div className="summary-card full-width">
                        <h4>Notes</h4>
                        <p className="notes-text">{selectedBOM.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" data-testid="print-bom">
                <Printer size={14} /> Print
              </button>
              <button className="btn btn-secondary" data-testid="download-bom">
                <Download size={14} /> Export
              </button>
              {selectedBOM.status === 'completed' && (
                <button className="btn btn-primary" data-testid="generate-invoice">
                  <FileText size={14} /> Generate Invoice
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create BOM Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New BOM</h3>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>BOM Code</label>
                <input 
                  type="text" 
                  placeholder="Auto-generated"
                  disabled
                  value={`BOM-${new Date().toISOString().split('T')[0]}`}
                />
              </div>
              <div className="form-group">
                <label>Link to Manifest</label>
                <select data-testid="select-manifest">
                  <option value="">Select manifest...</option>
                  <option value="MAN-2025-12-16">MAN-2025-12-16 (Today)</option>
                  <option value="MAN-2025-12-15">MAN-2025-12-15 (Yesterday)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Link to Intake Session</label>
                <select data-testid="select-session">
                  <option value="">Select session...</option>
                  <option value="sess-001">Dec 15, 2025 - 47 packages</option>
                  <option value="sess-002">Dec 14, 2025 - 82 packages</option>
                </select>
              </div>
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea 
                  placeholder="Add any notes..."
                  rows={3}
                  data-testid="bom-notes"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" data-testid="create-bom-confirm">
                <Plus size={14} /> Create BOM
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
